import {
  ChevronRightIcon,
  InfoCircledIcon,
  LockClosedIcon,
  LockOpen1Icon,
} from '@radix-ui/react-icons';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../primitives/accordion';
import { Badge } from '../../../primitives/badge';
import { Button } from '../../../primitives/button';
import { ButtonGroup } from '../../../primitives/button-group';
import { Input } from '../../../primitives/input';
import { Label } from '../../../primitives/label';
import { Separator } from '../../../primitives/separator';
import { SliderWithBadge } from '../../../primitives/slider';
import { Switch } from '../../../primitives/switch';
import { ComboboxModel } from '../../combobox/combobox-model';
import { LabelWithoutRef } from '../../label/label-without-ref';

import { FlipHorizontal2Icon } from '../../../svg/flip-horizontal-2-icon';
import { cn } from '../../../utils/cn';

import { bucketsToRanges } from './utils/buckets-to-ranges';
import { normalizeRanges } from './utils/normalize-ranges';
import { rangesToBuckets } from './utils/ranges-to-buckets';

import { InputId } from '../input-id';
import { BucketButton } from './components/bucket-button';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../primitives/tooltip';
import { type BucketRange } from './_types';
import { CAT_BUCKETS, CAT_PART_MAP } from './cat.const';
import { GRID_QUADRANT_ORDER, QUADRANT_NEXT_ICONS, QUADRANTS } from './quadrants.const';

const BUCKET_TYPE = {
  rollout: 'rollout',
  bucket: 'bucket',
} as const;

export interface BucketInValue {
  lockedQuadrants?: number[];
  ranges: BucketRange[];
  salt?: string;
  saltBase?: string;
  type?: keyof typeof BUCKET_TYPE;
}

export interface InputBucketInProps {
  disabled?: boolean;
  readonly?: boolean;
  saltBaseOptions?: { attributeName: string; codeDataType?: string }[];
  value?: BucketInValue;
  onChange: (value: BucketInValue) => void;
  onSaltBaseChange?: (saltBase: string) => void;
}

export interface InputBucketInRef {
  // Cancels any in-progress selection (click-range anchor or drag) without committing changes.
  cancelRange: VoidFunction;
}

/**
 * Controlled 10×10 bucket selection grid with a shortcuts panel.
 *
 * Supports two interaction modes:
 * - **Click-range**: click an anchor bucket, then click another to toggle the
 *   rectangular range between them.
 * - **Drag-select**: hold the mouse button and sweep over buckets to toggle
 *   all visited cells at once.
 *
 * Quadrants can be individually locked to prevent their buckets from being
 * changed by shortcuts or selections.
 *
 * Expose a ref typed as {@link InputBucketInRef} to call `cancelRange()`
 * imperatively (e.g. when the host popover closes).
 */
export const InputBucketIn = forwardRef<InputBucketInRef, InputBucketInProps>(
  function InputBucketIn(
    {
      disabled = false,
      readonly = false,
      saltBaseOptions,
      value = { ranges: [], lockedQuadrants: [], saltBase: '' },
      onChange,
      onSaltBaseChange,
    },
    ref,
  ) {
    const normalizedRanges = useMemo(() => normalizeRanges(value.ranges), [value.ranges]);
    const activeBuckets = useMemo(() => rangesToBuckets(normalizedRanges), [normalizedRanges]);
    const activeBucketsSet = useMemo(() => new Set(activeBuckets), [activeBuckets]);

    const lockedQuadrants = useMemo(
      () => new Set(value.lockedQuadrants ?? []),
      [value.lockedQuadrants],
    );
    const lockedBucketsSet = useMemo(() => {
      const bucketSet = new Set<number>();
      for (const quadrantIndex of lockedQuadrants) {
        for (const bucket of QUADRANTS[quadrantIndex]) bucketSet.add(bucket);
      }
      return bucketSet;
    }, [lockedQuadrants]);

    const isCatMode = useMemo(
      () =>
        activeBuckets.length === CAT_BUCKETS.length &&
        CAT_BUCKETS.every((bucket) => activeBucketsSet.has(bucket)),
      [activeBuckets.length, activeBucketsSet],
    );

    const allSelected = activeBuckets.length === 100;
    const allQuadrantsLocked = lockedQuadrants.size === 4;

    /** First click of a range selection — null when no selection is in progress */
    const [rangeStart, setRangeStart] = useState<number | null>(null);
    /** Bucket currently under the pointer while a range selection is in progress */
    const [hoverBucket, setHoverBucket] = useState<number | null>(null);

    /**
     * ---
     * Drag-select state
     * ---
     */

    /** True while the primary mouse button is held down */
    const isMouseDownRef = useRef(false);
    /** Buckets visited during the current drag (empty when not dragging) */
    const dragBucketsRef = useRef(new Set<number>());
    const [dragBuckets, setDragBuckets] = useState<Set<number>>(new Set());

    /** Refs so the global mouseup effect never needs to be re-registered */
    const activeBucketsRef = useRef(activeBuckets);
    activeBucketsRef.current = activeBuckets;
    const rangeStartRef = useRef(rangeStart);
    rangeStartRef.current = rangeStart;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const valueRef = useRef(value);
    valueRef.current = value;
    const lockedBucketsSetRef = useRef(new Set<number>());
    lockedBucketsSetRef.current = lockedBucketsSet;

    /**
     * ---
     * Derived preview buckets (shown while user is mid-selection)
     * ---
     */

    const previewBuckets = useMemo<Set<number>>(() => {
      // Drag in progress → preview exactly the visited buckets
      if (dragBuckets.size > 0) return dragBuckets;
      // Click-range in progress → preview the rectangular range
      if (rangeStart === null || hoverBucket === null) return new Set();
      const rangeMin = Math.min(rangeStart, hoverBucket);
      const rangeMax = Math.max(rangeStart, hoverBucket);
      const previewSet = new Set<number>();
      for (let bucketIndex = rangeMin; bucketIndex <= rangeMax; bucketIndex++)
        previewSet.add(bucketIndex);
      return previewSet;
    }, [dragBuckets, rangeStart, hoverBucket]);

    /** True when all previewed buckets are already active → the action will deselect them */
    const willDeselect = useMemo(
      () =>
        previewBuckets.size > 0 &&
        [...previewBuckets].every((bucket) => activeBucketsSet.has(bucket)),
      [previewBuckets, activeBucketsSet],
    );

    /**
     * ---
     * Grid interaction
     * ---
     */

    const handleBucketMouseDown = useCallback((bucket: number) => {
      isMouseDownRef.current = true;
      const initialDragSet = new Set<number>([bucket]);
      dragBucketsRef.current = initialDragSet;
      setDragBuckets(new Set(initialDragSet));
    }, []);

    const handleBucketMouseEnter = useCallback(
      (bucket: number) => {
        if (isMouseDownRef.current) {
          // Drag mode — accumulate individual buckets
          const updatedDragSet = new Set([...dragBucketsRef.current, bucket]);
          dragBucketsRef.current = updatedDragSet;
          setDragBuckets(new Set(updatedDragSet));
        } else if (rangeStart !== null) {
          // Click-range mode — update hover for preview
          setHoverBucket(bucket);
        }
      },
      [rangeStart],
    );

    const cancelRange = useCallback(() => {
      setRangeStart(null);
      setHoverBucket(null);
      isMouseDownRef.current = false;
      dragBucketsRef.current = new Set();
      setDragBuckets(new Set());
    }, []);

    useImperativeHandle(ref, () => ({ cancelRange }), [cancelRange]);

    const toggleQuadrantLock = useCallback(
      (quadrantIndex: number) => {
        const updatedLocks = new Set(lockedQuadrants);
        if (updatedLocks.has(quadrantIndex)) updatedLocks.delete(quadrantIndex);
        else updatedLocks.add(quadrantIndex);
        onChange({ ...value, lockedQuadrants: [...updatedLocks] });
      },
      [lockedQuadrants, onChange, value],
    );

    /**
     * ---
     * Shortcut handlers
     * ---
     */

    const [activeQuadrant, setActiveQuadrant] = useState<number | null>(null);
    const [randomPct, setRandomPct] = useState(50);
    const [saltLocked, setSaltLocked] = useState(() => !!value.salt);
    const [rolloutPct, setRolloutPct] = useState<number>(() => {
      if (value.type === BUCKET_TYPE.rollout) {
        const buckets = rangesToBuckets(normalizeRanges(value.ranges));
        return buckets.length || 50;
      }
      return 50;
    });
    const currentType = value.type ?? BUCKET_TYPE.rollout;
    const saltBaseModel = useMemo(
      () =>
        (saltBaseOptions ?? []).map((option) => ({
          key: option.attributeName,
          value: option.attributeName,
          item: (
            <span className="flex w-full items-center justify-between gap-1">
              <span className="grid">
                <span title={option.attributeName} className="truncate">
                  {option.attributeName}
                </span>
              </span>
              {option.codeDataType && (
                <Badge className="shrink-0 p-0 px-1.5 text-[10px]" variant="success">
                  {option.codeDataType}
                </Badge>
              )}
            </span>
          ),
        })),
      [saltBaseOptions],
    );

    const handleTypeChange = useCallback(
      (newType: keyof typeof BUCKET_TYPE) => {
        if (newType === BUCKET_TYPE.rollout) {
          const selectedBucketsCount = Math.max(
            1,
            Math.min(100, activeBuckets.length > 0 ? activeBuckets.length : 50),
          );
          setRolloutPct(selectedBucketsCount);
          onChange({
            ...value,
            type: BUCKET_TYPE.rollout,
            lockedQuadrants: [],
            ranges: bucketsToRanges(
              Array.from({ length: selectedBucketsCount }, (_, bucketIndex) => bucketIndex),
            ),
          });
        } else {
          onChange({ ...value, type: BUCKET_TYPE.bucket, ranges: [] });
        }
      },
      [activeBuckets.length, onChange, value],
    );

    /** Returns `desired` with locked buckets replaced by their current state */
    const withLocked = useCallback(
      (desired: number[]) => [
        ...desired.filter((bucket) => !lockedBucketsSet.has(bucket)),
        ...activeBuckets.filter((bucket) => lockedBucketsSet.has(bucket)),
      ],
      [activeBuckets, lockedBucketsSet],
    );

    const handleSelectAll = useCallback(() => {
      setActiveQuadrant(null);
      onChange({
        ...valueRef.current,
        ranges: bucketsToRanges(
          withLocked(Array.from({ length: 100 }, (_, bucketIndex) => bucketIndex)),
        ),
      });
      cancelRange();
    }, [withLocked, onChange, cancelRange]);

    const handleInvert = useCallback(() => {
      setActiveQuadrant(null);
      const inverted = Array.from({ length: 100 }, (_, bucketIndex) => bucketIndex).filter(
        (bucket) => !activeBucketsSet.has(bucket) && !lockedBucketsSet.has(bucket),
      );
      onChange({ ...valueRef.current, ranges: bucketsToRanges(withLocked(inverted)) });
      cancelRange();
    }, [activeBucketsSet, lockedBucketsSet, withLocked, onChange, cancelRange]);

    const handleSelectHalf = useCallback(() => {
      setActiveQuadrant(null);
      onChange({
        ...valueRef.current,
        ranges: bucketsToRanges(
          withLocked(Array.from({ length: 50 }, (_, bucketIndex) => bucketIndex)),
        ),
      });
      cancelRange();
    }, [withLocked, onChange, cancelRange]);

    const handleSelectEven = useCallback(() => {
      setActiveQuadrant(null);
      // Visual "pares": buckets 2,4,6,...100 → internal indices 1,3,5,...,99
      onChange({
        ...valueRef.current,
        ranges: bucketsToRanges(
          withLocked(Array.from({ length: 50 }, (_, bucketIndex) => bucketIndex * 2 + 1)),
        ),
      });
      cancelRange();
    }, [withLocked, onChange, cancelRange]);

    const handleSelectQuarter = useCallback(() => {
      const firstUnlocked = [0, 1, 2, 3].find((q) => !lockedQuadrants.has(q));
      if (firstUnlocked === undefined) return;
      setActiveQuadrant(firstUnlocked);
      onChange({
        ...valueRef.current,
        ranges: bucketsToRanges(withLocked([...QUADRANTS[firstUnlocked]])),
      });
      cancelRange();
    }, [lockedQuadrants, withLocked, onChange, cancelRange]);

    const handleSelectRandom = useCallback(() => {
      setActiveQuadrant(null);
      const unlockedBuckets = Array.from({ length: 100 }, (_, bucketIndex) => bucketIndex).filter(
        (bucket) => !lockedBucketsSet.has(bucket),
      );
      const lockedSelectedCount = activeBuckets.filter((bucket) =>
        lockedBucketsSet.has(bucket),
      ).length;
      const targetUnlockedCount = Math.max(
        0,
        Math.min(unlockedBuckets.length, randomPct - lockedSelectedCount),
      );

      // Shuffle unlocked buckets and take exactly the required amount.
      const shuffledBuckets = [...unlockedBuckets];
      for (let index = shuffledBuckets.length - 1; index > 0; index--) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [shuffledBuckets[index], shuffledBuckets[randomIndex]] = [
          shuffledBuckets[randomIndex],
          shuffledBuckets[index],
        ];
      }

      const random = shuffledBuckets.slice(0, targetUnlockedCount);
      onChange({ ...valueRef.current, ranges: bucketsToRanges(withLocked(random)) });
      cancelRange();
    }, [randomPct, activeBuckets, lockedBucketsSet, withLocked, onChange, cancelRange]);

    const handleCat = useCallback(() => {
      setActiveQuadrant(null);
      onChange({
        ...valueRef.current,
        ranges: bucketsToRanges(withLocked(CAT_BUCKETS)),
      });
      cancelRange();
    }, [withLocked, onChange, cancelRange]);

    const handleNextQuadrant = useCallback(() => {
      const startQuadrant = activeQuadrant ?? -1;
      // Find the next unlocked quadrant (clockwise)
      let nextQuadrant = (startQuadrant + 1) % 4;
      let cycleAttempts = 0;
      while (lockedQuadrants.has(nextQuadrant) && cycleAttempts < 4) {
        nextQuadrant = (nextQuadrant + 1) % 4;
        cycleAttempts++;
      }
      if (cycleAttempts >= 4) return; // All quadrants locked
      // Remove the current unlocked quadrant from the base selection before adding the next
      let baseSelection = activeBuckets;
      if (activeQuadrant !== null && !lockedQuadrants.has(activeQuadrant)) {
        const currentQuadrantBuckets = new Set(QUADRANTS[activeQuadrant]);
        baseSelection = baseSelection.filter((bucket) => !currentQuadrantBuckets.has(bucket));
      }
      const mergedBuckets = [...new Set([...baseSelection, ...QUADRANTS[nextQuadrant]])];
      setActiveQuadrant(nextQuadrant);
      onChange({ ...valueRef.current, ranges: bucketsToRanges(mergedBuckets) });
      cancelRange();
    }, [activeQuadrant, activeBuckets, lockedQuadrants, onChange, cancelRange]);

    /**
     * ---
     * Global mouseup — commits drag or single-click
     * ---
     */

    useEffect(() => {
      const handleMouseUp = () => {
        if (!isMouseDownRef.current) return;
        isMouseDownRef.current = false;

        const dragSet = dragBucketsRef.current;
        const currentActive = activeBucketsRef.current;
        const lockedBuckets = lockedBucketsSetRef.current;
        // Preserve locked buckets: keep their current state regardless of what was computed
        const applyLocked = (computed: number[]) => [
          ...computed.filter((bucket) => !lockedBuckets.has(bucket)),
          ...currentActive.filter((bucket) => lockedBuckets.has(bucket)),
        ];

        if (dragSet.size > 1) {
          // Drag mode: toggle exactly the visited buckets
          const allAlreadyActive = [...dragSet].every((bucket) => currentActive.includes(bucket));
          const toggled = allAlreadyActive
            ? currentActive.filter((bucket) => !dragSet.has(bucket))
            : [...new Set([...currentActive, ...dragSet])];
          onChangeRef.current({
            ...valueRef.current,
            ranges: bucketsToRanges(applyLocked(toggled)),
          });
          setRangeStart(null);
          setHoverBucket(null);
        } else if (dragSet.size === 1) {
          // Single click — existing two-click range behavior
          const [clickedBucket] = dragSet;
          const currentRangeStart = rangeStartRef.current;
          if (currentRangeStart === null) {
            setRangeStart(clickedBucket);
            setHoverBucket(clickedBucket);
          } else {
            const rangeMin = Math.min(currentRangeStart, clickedBucket);
            const rangeMax = Math.max(currentRangeStart, clickedBucket);
            const rangeSet = new Set<number>();
            for (let bucketIndex = rangeMin; bucketIndex <= rangeMax; bucketIndex++)
              rangeSet.add(bucketIndex);
            const allAlreadyActive = [...rangeSet].every((bucket) =>
              currentActive.includes(bucket),
            );
            const toggled = allAlreadyActive
              ? currentActive.filter((bucket) => !rangeSet.has(bucket))
              : [...new Set([...currentActive, ...rangeSet])];
            onChangeRef.current({
              ...valueRef.current,
              ranges: bucketsToRanges(applyLocked(toggled)),
            });
            setRangeStart(null);
            setHoverBucket(null);
          }
        }

        dragBucketsRef.current = new Set();
        setDragBuckets(new Set());
      };

      document.addEventListener('mouseup', handleMouseUp);
      return () => document.removeEventListener('mouseup', handleMouseUp);
    }, []);

    return (
      <div className="flex w-full flex-col gap-6">
        {/* Seed form */}
        <div className="grid gap-2">
          <div className="flex items-center gap-2">
            <LabelWithoutRef>Semilla de segmentación</LabelWithoutRef>
            <p className="text-[0.75rem] text-muted-foreground">(Fact + Salt)</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger tabIndex={-1}>
                  <InfoCircledIcon className="size-4 text-accent" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[20rem] space-y-2 p-4 text-muted-foreground">
                  <p>Valor para asegurar una segmentación de usuarios determinista.</p>
                  <p>Cambiar el fact o el salt redistribuirá a los usuarios en nuevos segmentos.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex flex-col gap-4 md:flex-row">
            {value.saltBase &&
              (saltBaseModel.length > 0 && onSaltBaseChange ? (
                <ComboboxModel
                  id="segment-salt1"
                  className="w-full md:w-[15rem]"
                  disabled={disabled || readonly}
                  model={saltBaseModel}
                  placeholder="Seleccionar fact"
                  size="xs"
                  value={value.saltBase}
                  variant="outline"
                  onSelect={onSaltBaseChange}
                />
              ) : (
                <Input
                  id="segment-salt1"
                  className="w-full md:w-[15rem]"
                  disabled={disabled || readonly}
                  value={value.saltBase}
                />
              ))}
            <div className="relative flex-1">
              <InputId
                button={{ label: 'Auto generar' }}
                counter={{ enable: true }}
                disabled={disabled || readonly || saltLocked}
                id="segment-salt2"
                maxLength={50}
                placeholder="Salt (ej. high-conversion seed, 1a05f...)"
                value={value.salt ?? ''}
                onChange={(e) =>
                  onChange({
                    ...value,
                    salt: e.target.value.trim().replace(/\s+/g, '_') || undefined,
                  })
                }
              />
              <button
                aria-label={saltLocked ? 'Desbloquear semilla' : 'Bloquear semilla'}
                className={cn(
                  'absolute -top-2.5 -right-3 z-10 flex size-6 items-center justify-center rounded-full transition-colors duration-200 ease-in-out',
                  saltLocked
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  {
                    'cursor-not-allowed': disabled || readonly,
                  },
                )}
                title={saltLocked ? 'Desbloquear semilla' : 'Bloquear semilla'}
                type="button"
                onClick={() => setSaltLocked((prev) => !prev)}
                disabled={disabled || readonly}
              >
                {saltLocked ? (
                  <LockClosedIcon className="size-3" />
                ) : (
                  <LockOpen1Icon className="size-3" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Segment type selection */}
        <div className="grid gap-2">
          <div className="flex items-center gap-2">
            <LabelWithoutRef>Segmentación</LabelWithoutRef>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger tabIndex={-1}>
                  <InfoCircledIcon className="size-4 text-accent" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[20rem] space-y-2 p-4 text-muted-foreground">
                  <p>
                    <span className="text-accent">Rollout</span> gradual del 1% al 100% <br />
                    (de izquierda a derecha).
                  </p>
                  <p>
                    <span className="text-accent">Por bucket</span> segmentación personalizada
                    <br />
                    (1 bucket = 1% de los usuarios).
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <Accordion
            collapsible
            type="single"
            value={currentType}
            onValueChange={(item) => {
              const nextType = (item ||
                (currentType === BUCKET_TYPE.rollout
                  ? BUCKET_TYPE.bucket
                  : BUCKET_TYPE.rollout)) as keyof typeof BUCKET_TYPE;

              handleTypeChange(nextType);
            }}
          >
            <AccordionItem value={BUCKET_TYPE.rollout}>
              <AccordionTrigger
                asChild
                className="px-0 py-1 [&[data-state=open]>button]:bg-accent/50 [&[data-state=open]>button]:text-accent-foreground [&[data-state=open]>button>svg.chevron]:rotate-90"
              >
                <Button
                  disabled={disabled || readonly}
                  className="flex-auto justify-between gap-4 select-none"
                  variant="ghost"
                  size="lg"
                >
                  <p className="grid">
                    <span className="truncate text-sm font-normal">Rollout</span>
                  </p>
                  <ChevronRightIcon className="chevron transition-transform duration-200" />
                </Button>
              </AccordionTrigger>

              <AccordionContent className="py-6">
                <div className="ml-[1.1rem] grid gap-6 border-l-2 border-dotted pl-10">
                  {/* Instructions */}
                  <p className="text-xs leading-relaxed text-pretty">
                    <span className="font-bold text-accent">Activación gradual</span> de funciones
                    iniciando con un 1% de la audiencia. Este proceso permite validar la experiencia
                    del usuario y la estabilidad del sistema antes del lanzamiento global (100%).
                  </p>

                  <div className="grid gap-4 pr-4">
                    <div>
                      <SliderWithBadge
                        badgeFormatter={(valueNumber) => `${valueNumber}%`}
                        disabled={disabled || readonly}
                        max={100}
                        min={1}
                        value={rolloutPct}
                        onValueChange={(pct) => {
                          setRolloutPct(pct);
                          onChange({
                            ...value,
                            type: BUCKET_TYPE.rollout,
                            lockedQuadrants: [],
                            ranges: bucketsToRanges(Array.from({ length: pct }, (_, i) => i)),
                          });
                        }}
                      />

                      {/* Tick marks at 25 %, 50 % and 75 % */}
                      {/* <div className="relative h-4">
                        {([25, 50, 75] as const).map((tick) => (
                          <button
                            key={tick}
                            aria-label={`Ir al ${tick}%`}
                            className="absolute top-0 h-8 w-6 -translate-x-1/2 -translate-y-2 cursor-pointer"
                            disabled={disabled || readonly}
                            style={{
                              left: `calc(${((tick - 1) / 99) * 100}% - ${((tick - 1) / 99) * 16 - 8}px)`,
                            }}
                            type="button"
                            onClick={() => {
                              setRolloutPct(tick);
                              onChange({
                                ...value,
                                type: BUCKET_TYPE.rollout,
                                lockedQuadrants: [],
                                ranges: bucketsToRanges(Array.from({ length: tick }, (_, i) => i)),
                              });
                            }}
                          >
                            <div
                              className={cn(
                                'mx-auto w-px',
                                tick === 50
                                  ? 'bg-primary h-3'
                                  : 'bg-muted-foreground/50 hover:bg-muted-foreground h-2',
                              )}
                            />
                          </button>
                        ))}
                      </div> */}
                    </div>

                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>1 %</span>
                      <span>100 %</span>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem className="last:border-0" value={BUCKET_TYPE.bucket}>
              <AccordionTrigger
                asChild
                className="px-0 py-1 [&[data-state=open]>button]:bg-accent/50 [&[data-state=open]>button]:text-accent-foreground [&[data-state=open]>button>svg.chevron]:rotate-90"
              >
                <Button
                  disabled={disabled || readonly}
                  className="flex-auto justify-between gap-4 select-none"
                  variant="ghost"
                  size="lg"
                >
                  <p className="grid">
                    <span className="truncate text-sm font-normal">Por bucket</span>
                  </p>
                  <ChevronRightIcon className="chevron transition-transform duration-200" />
                </Button>
              </AccordionTrigger>

              <AccordionContent className="py-6">
                <div className="ml-[1.1rem] grid gap-6 border-l-2 border-dotted pl-10">
                  {/* Instructions */}
                  <p className="text-xs leading-relaxed text-pretty">
                    <span className="font-bold text-accent">Haz clic</span> en un bucket para
                    iniciar un segmento. Luego selecciona otro para completarlo
                    <br />
                    <span className="font-bold text-accent">o haz clic y arrastra</span> para
                    seleccionar varios buckets rápidamente.
                  </p>

                  <div className="flex gap-8 pr-3 max-sm:flex-col">
                    {/* Shortcuts */}
                    <div className="flex-1 space-y-1.5">
                      <LabelWithoutRef className="text-[0.75rem] text-muted-foreground">
                        Selección rápida
                      </LabelWithoutRef>

                      {/* All / clean */}
                      <ButtonGroup className="w-full">
                        <Button
                          className="flex-1"
                          disabled={disabled || allSelected || readonly}
                          size="sm"
                          title="Seleccionar todos los buckets"
                          variant="outline"
                          onClick={handleSelectAll}
                        >
                          Todo
                        </Button>
                        <Button
                          className="flex-1"
                          disabled={
                            disabled ||
                            activeBuckets.every((bucket) => lockedBucketsSet.has(bucket)) ||
                            readonly
                          }
                          size="sm"
                          title="Limpiar selección actual de buckets"
                          variant="outline"
                          onClick={() => {
                            onChange({
                              ...value,
                              ranges: bucketsToRanges(
                                activeBuckets.filter((bucket) => lockedBucketsSet.has(bucket)),
                              ),
                            });
                            cancelRange();
                          }}
                        >
                          Limpiar
                        </Button>
                      </ButtonGroup>

                      {/* 1/4 + next Q */}
                      <ButtonGroup className="w-full">
                        <Button
                          className="flex-1"
                          disabled={disabled || readonly}
                          size="sm"
                          title="Seleccionar primer cuadrante (25%)"
                          variant="outline"
                          onClick={handleSelectQuarter}
                        >
                          1/4
                        </Button>
                        <Button
                          className="flex-1"
                          disabled={disabled || readonly}
                          size="sm"
                          title={`Seleccionar siguiente cuadrante (en sentido horario) Q${(((activeQuadrant ?? 0) + 1) % 4) + 1}`}
                          variant="outline"
                          onClick={handleNextQuadrant}
                        >
                          {(() => {
                            const NextIcon = QUADRANT_NEXT_ICONS[activeQuadrant ?? 0];
                            const nextQuadrantNumber = (((activeQuadrant ?? 0) + 1) % 4) + 1;
                            return (
                              <>
                                <NextIcon className="size-3" />
                                <span className="inline-block w-4">
                                  Q<sup>{nextQuadrantNumber}</sup>
                                </span>
                              </>
                            );
                          })()}
                        </Button>
                      </ButtonGroup>

                      {/* 1/2 + Even */}
                      <ButtonGroup className="w-full">
                        <Button
                          className="flex-1"
                          disabled={disabled || readonly}
                          size="sm"
                          title="Seleccionar primera mitad (50%)"
                          variant="outline"
                          onClick={handleSelectHalf}
                        >
                          1/2
                        </Button>
                        <Button
                          className="flex-1"
                          disabled={disabled || readonly}
                          size="sm"
                          title="Seleccionar primera mitad (50%)"
                          variant="outline"
                          onClick={handleCat}
                        >
                          <span aria-label="emoji" role="img">
                            🐈
                          </span>
                        </Button>
                        <Button
                          className="flex-1"
                          disabled={disabled || readonly}
                          size="sm"
                          title="Seleccionar buckets pares"
                          variant="outline"
                          onClick={handleSelectEven}
                        >
                          Par
                        </Button>
                      </ButtonGroup>

                      {/* Invert */}
                      <Button
                        className="w-full"
                        disabled={disabled || readonly}
                        size="sm"
                        title="Invertir selección"
                        variant="outline"
                        onClick={handleInvert}
                      >
                        <FlipHorizontal2Icon />
                        Invertir
                      </Button>

                      <Separator className="my-4!" orientation="horizontal" />

                      {/* Random */}
                      <div className="grid gap-1">
                        <LabelWithoutRef className="text-[0.75rem] text-muted-foreground">
                          Probabilidad
                        </LabelWithoutRef>
                        <SliderWithBadge
                          badgeFormatter={(valueNumber) => `${valueNumber}%`}
                          disabled={disabled || readonly}
                          max={99}
                          min={1}
                          value={randomPct}
                          onValueChange={(valueNumber) => setRandomPct(valueNumber)}
                        />
                        <Button
                          className="w-full"
                          disabled={disabled || readonly}
                          size="sm"
                          title={`Seleccionar buckets al azar (${randomPct}% de probabilidad)`}
                          variant="outline"
                          onClick={handleSelectRandom}
                        >
                          Probar
                        </Button>
                      </div>

                      <Separator className="my-5!" orientation="horizontal" />

                      {/* Lock all quadrants */}
                      <div className="flex items-center justify-between gap-2">
                        <Label
                          className="cursor-pointer text-xs text-muted-foreground"
                          htmlFor="lock-all"
                        >
                          Bloquear todo
                        </Label>
                        <Switch
                          checked={allQuadrantsLocked}
                          disabled={disabled || readonly}
                          id="lock-all"
                          onCheckedChange={(checked) =>
                            onChange({ ...value, lockedQuadrants: checked ? [0, 1, 2, 3] : [] })
                          }
                        />
                      </div>
                    </div>

                    {/* Grid 10x10 */}
                    <div className="flex flex-none flex-col gap-5">
                      <div className="m-auto inline-block rounded-[1.2rem] border border-input p-4 shadow-xs">
                        <div className="relative size-[320px]">
                          {/* Vertical divider — between cols 5 and 6 */}
                          <div className="pointer-events-none absolute inset-y-0 left-1/2 ml-[1px] w-0 -translate-x-px border-l border-dotted border-muted" />
                          {/* Horizontal divider — between rows 5 and 6 */}
                          <div className="pointer-events-none absolute inset-x-0 top-1/2 mt-[1px] h-0 -translate-y-px border-t border-dotted border-muted" />
                          {/* Lock buttons — one per quadrant at each corner of the full grid */}
                          {(
                            [
                              [0, '-top-6 -left-6'],
                              [1, '-top-6 -right-6'],
                              [2, '-bottom-6 -right-6'],
                              [3, '-bottom-6 -left-6'],
                            ] as const
                          ).map(([quadrantIndex, cornerPosition]) => {
                            const isQuadrantLocked = lockedQuadrants.has(quadrantIndex);
                            return (
                              <button
                                key={quadrantIndex}
                                aria-label={
                                  isQuadrantLocked
                                    ? `Desbloquear cuadrante ${quadrantIndex + 1}`
                                    : `Bloquear cuadrante ${quadrantIndex + 1}`
                                }
                                className={cn(
                                  'absolute z-10 flex size-6 items-center justify-center rounded-full transition-colors duration-200 ease-in-out',
                                  cornerPosition,
                                  isQuadrantLocked
                                    ? 'bg-accent text-accent-foreground'
                                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                                  {
                                    'cursor-not-allowed': disabled || readonly,
                                  },
                                )}
                                onClick={() => toggleQuadrantLock(quadrantIndex)}
                                type="button"
                                disabled={disabled || readonly}
                              >
                                {isQuadrantLocked ? (
                                  <LockClosedIcon className="size-3" />
                                ) : (
                                  <LockOpen1Icon className="size-3" />
                                )}
                              </button>
                            );
                          })}
                          {/* Each cell is a 5×5 quadrant */}
                          <div className="grid grid-cols-2 gap-4">
                            {GRID_QUADRANT_ORDER.map((quadrantIndex) => {
                              const isQuadrantLocked = lockedQuadrants.has(quadrantIndex);
                              return (
                                <div
                                  key={quadrantIndex}
                                  className="grid grid-cols-5 justify-items-center gap-2"
                                >
                                  {QUADRANTS[quadrantIndex].map((bucketIndex) => (
                                    <BucketButton
                                      key={bucketIndex}
                                      catPart={
                                        isCatMode ? CAT_PART_MAP.get(bucketIndex) : undefined
                                      }
                                      disabled={disabled || readonly}
                                      index={bucketIndex}
                                      isActive={activeBucketsSet.has(bucketIndex)}
                                      isLocked={isQuadrantLocked}
                                      isPreview={previewBuckets.has(bucketIndex)}
                                      isStart={rangeStart === bucketIndex}
                                      willDeselect={willDeselect}
                                      onMouseDown={handleBucketMouseDown}
                                      onMouseEnter={handleBucketMouseEnter}
                                    />
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Grid label */}
                      <p className="text-center text-xs leading-relaxed">
                        Porcentaje seleccionado:
                        <span className="inline-block w-[3rem] text-end font-bold text-primary tabular-nums">
                          {activeBuckets.length} %
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    );
  },
);

InputBucketIn.displayName = 'InputBucketIn';
